import React, { useState } from 'react';
import { BarChart2, Download, Info, Heart, Activity, ZoomIn } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент визуализации целевых зон пульса
 */
const TargetZonesVisualization = ({ results, exerciseType, exerciseTypes }) => {
  const [activeTab, setActiveTab] = useState('chart');
  
  if (!results) {
    return null;
  }

  // Находим объект типа тренировки
  const exerciseTypeObj = exerciseTypes.find(type => type.id === exerciseType) || exerciseTypes[0];

  // Определяем цвета для зон (от зеленого к красному)
  const zoneColors = [
    'bg-green-500 dark:bg-green-600',
    'bg-blue-500 dark:bg-blue-600',
    'bg-yellow-500 dark:bg-yellow-600',
    'bg-orange-500 dark:bg-orange-600',
    'bg-red-500 dark:bg-red-600'
  ];

  // Определяем размер графика
  const chartWidth = 100; // в процентах
  const maxPulse = results.maxHeartRate + 10;
  const minPulse = Math.max(results.restingHeartRate ? (results.restingHeartRate - 20) : 40, 40);
  const pulseRange = maxPulse - minPulse;

  // Функция для расчета позиции на шкале в %
  const getPositionPercent = (heartRate) => {
    return ((heartRate - minPulse) / pulseRange) * 100;
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-5 mt-8">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <BarChart2 className="w-5 h-5 mr-2 text-red-500" />
          Визуализация зон пульса
        </h3>

        {/* Кнопка экспорта диаграммы */}
        <Button
          size="sm"
          onClick={() => alert('Функционал сохранения изображения находится в разработке')}
          variant="outline"
          icon={<Download className="w-4 h-4" />}
        >
          Сохранить диаграмму
        </Button>
      </div>

      {/* Вкладки для разных типов отображения */}
      <div className="flex space-x-1 border-b border-gray-200 dark:border-gray-700 mb-4">
        <button
          onClick={() => setActiveTab('chart')}
          className={`py-2 px-4 text-sm font-medium rounded-t-lg ${
            activeTab === 'chart'
              ? 'text-red-600 dark:text-red-400 border-b-2 border-red-500 dark:border-red-400'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          Диаграмма зон
        </button>
        <button
          onClick={() => setActiveTab('table')}
          className={`py-2 px-4 text-sm font-medium rounded-t-lg ${
            activeTab === 'table'
              ? 'text-red-600 dark:text-red-400 border-b-2 border-red-500 dark:border-red-400'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          Таблица зон
        </button>
      </div>

      {/* Объяснение зон пульса */}
      <div className="bg-gray-50 dark:bg-gray-700/30 border border-gray-200 dark:border-gray-700 rounded p-3 mb-4 flex items-start">
        <Info className="text-blue-500 dark:text-blue-400 w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-gray-700 dark:text-gray-300">
          <p>
            Диаграмма показывает пять зон интенсивности: от зоны восстановления (50-60%) до максимальной зоны (90-100%).
            {exerciseTypeObj && ` Оптимальная зона для ${exerciseTypeObj.name.toLowerCase()} выделена ярче.`}
          </p>
        </div>
      </div>

      {/* Диаграмма зон пульса */}
      {activeTab === 'chart' && (
        <div className="heart-rate-chart overflow-x-auto">
          <div className="min-w-[600px]">
            {/* Шкала пульса */}
            <div className="relative h-10 mb-1">
              {/* Линии разметки */}
              {[50, 100, 150, 200].map((pulse) => (
                <div 
                  key={pulse}
                  className="absolute bottom-0 h-3 border-l border-gray-300 dark:border-gray-600"
                  style={{
                    left: `${getPositionPercent(pulse)}%`,
                    display: pulse >= minPulse && pulse <= maxPulse ? 'block' : 'none'
                  }}
                ></div>
              ))}

              {/* Метки пульса */}
              {[50, 75, 100, 125, 150, 175, 200].map((pulse) => (
                pulse >= minPulse && pulse <= maxPulse && (
                  <div 
                    key={pulse}
                    className="absolute bottom-0 text-xs text-gray-500 dark:text-gray-400"
                    style={{
                      left: `${getPositionPercent(pulse)}%`,
                      transform: 'translateX(-50%)'
                    }}
                  >
                    {pulse}
                  </div>
                )
              ))}

              {/* Максимальный пульс метка */}
              <div 
                className="absolute bottom-8 flex items-center"
                style={{
                  left: `${getPositionPercent(results.maxHeartRate)}%`,
                  transform: 'translateX(-50%)'
                }}
              >
                <div className="px-2 py-0.5 bg-red-100 dark:bg-red-900/40 border border-red-300 dark:border-red-800 rounded text-xs text-red-700 dark:text-red-300 whitespace-nowrap">
                  Макс. пульс: {results.maxHeartRate}
                </div>
                <div className="h-3 w-0.5 bg-red-400 dark:bg-red-600 mt-0.5"></div>
              </div>

              {/* Пульс в покое метка (если есть) */}
              {results.restingHeartRate && (
                <div 
                  className="absolute bottom-8 flex items-center"
                  style={{
                    left: `${getPositionPercent(results.restingHeartRate)}%`,
                    transform: 'translateX(-50%)'
                  }}
                >
                  <div className="px-2 py-0.5 bg-green-100 dark:bg-green-900/40 border border-green-300 dark:border-green-800 rounded text-xs text-green-700 dark:text-green-300 whitespace-nowrap">
                    Пульс в покое: {results.restingHeartRate}
                  </div>
                  <div className="h-3 w-0.5 bg-green-400 dark:bg-green-600 mt-0.5"></div>
                </div>
              )}
            </div>

            {/* Шкала процентов */}
            <div className="h-6 relative">
              {[0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100].map((percent) => (
                <div 
                  key={percent}
                  className="absolute text-xs text-gray-500 dark:text-gray-400"
                  style={{
                    left: `${percent}%`,
                    transform: 'translateX(-50%)'
                  }}
                >
                  {percent}%
                </div>
              ))}
            </div>

            {/* Зоны пульса */}
            <div className="h-44 mt-4 relative">
              {results.zones.map((zone, index) => {
                const startPercent = getPositionPercent(zone.lowerHR);
                const endPercent = getPositionPercent(zone.upperHR);
                const width = endPercent - startPercent;
                
                // Проверка, является ли эта зона целевой для выбранного типа тренировки
                const isTargetZone = (
                  zone.lowerPercent <= results.targetZone.lowerPercent && 
                  zone.upperPercent >= results.targetZone.upperPercent
                ) || (
                  zone.lowerPercent >= results.targetZone.lowerPercent && 
                  zone.lowerPercent <= results.targetZone.upperPercent
                ) || (
                  zone.upperPercent >= results.targetZone.lowerPercent && 
                  zone.upperPercent <= results.targetZone.upperPercent
                );

                return (
                  <div 
                    key={index}
                    className={`absolute h-full rounded-lg ${zoneColors[index]} ${isTargetZone ? 'opacity-100 ring-2 ring-offset-2 ring-red-500 dark:ring-red-400' : 'opacity-70'}`}
                    style={{
                      left: `${startPercent}%`,
                      width: `${width}%`,
                    }}
                  >
                    <div className="absolute inset-0 flex flex-col justify-between p-3 text-white">
                      <div>
                        <h4 className="font-bold text-sm">{zone.name}</h4>
                        <p className="text-xs opacity-90 mt-1">{zone.lowerPercent}-{zone.upperPercent}%</p>
                        <p className="text-xs font-semibold mt-0.5">{zone.lowerHR}-{zone.upperHR} уд/мин</p>
                      </div>
                      <p className="text-xs opacity-90 line-clamp-2">{zone.description}</p>
                    </div>
                  </div>
                );
              })}

              {/* Указатель целевой зоны */}
              <div 
                className="absolute top-0 h-full pointer-events-none"
                style={{
                  left: `${getPositionPercent(results.targetZone.lowerHR)}%`,
                  width: `${getPositionPercent(results.targetZone.upperHR) - getPositionPercent(results.targetZone.lowerHR)}%`,
                  zIndex: 5
                }}
              >
                <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 px-2 py-1 bg-white dark:bg-gray-800 border border-red-300 dark:border-red-700 rounded-full text-xs text-red-600 dark:text-red-400 whitespace-nowrap shadow-sm">
                  Целевая зона: {results.targetZone.lowerHR}-{results.targetZone.upperHR} уд/мин
                </div>
                <div className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 px-2 py-1 bg-white dark:bg-gray-800 border border-red-300 dark:border-red-700 rounded-full text-xs text-red-600 dark:text-red-400 whitespace-nowrap shadow-sm">
                  {results.targetZone.lowerPercent}-{results.targetZone.upperPercent}%
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Таблица зон пульса */}
      {activeTab === 'table' && (
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Зона</th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Интенсивность (%)</th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Пульс (уд/мин)</th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Описание</th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {results.zones.map((zone, index) => {
                // Проверка, входит ли эта зона в целевую зону
                const isTargetZone = (
                  zone.lowerPercent <= results.targetZone.lowerPercent && 
                  zone.upperPercent >= results.targetZone.upperPercent
                ) || (
                  zone.lowerPercent >= results.targetZone.lowerPercent && 
                  zone.lowerPercent <= results.targetZone.upperPercent
                ) || (
                  zone.upperPercent >= results.targetZone.lowerPercent && 
                  zone.upperPercent <= results.targetZone.upperPercent
                );

                return (
                  <tr 
                    key={index} 
                    className={isTargetZone 
                      ? 'bg-red-50 dark:bg-red-900/20' 
                      : 'hover:bg-gray-50 dark:hover:bg-gray-700'}
                  >
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center">
                        <div className={`w-3 h-3 rounded-full ${zoneColors[index]} mr-2`}></div>
                        {zone.name}
                        {isTargetZone && (
                          <span className="ml-2 text-xs px-2 py-0.5 bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 rounded-full">
                            Рекомендуется
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                      {zone.lowerPercent}% - {zone.upperPercent}%
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                      {zone.lowerHR} - {zone.upperHR} уд/мин
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                      {zone.description}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Информация о применении */}
      <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded-lg">
        <div className="flex items-start gap-3">
          <Activity className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-1" />
          <div>
            <h4 className="text-sm font-medium text-blue-800 dark:text-blue-300 mb-2">
              Как использовать зоны пульса в тренировках
            </h4>
            <ul className="list-disc pl-5 text-sm text-blue-700 dark:text-blue-300 space-y-1">
              <li>Используйте пульсометр или фитнес-браслет для отслеживания частоты пульса в реальном времени</li>
              <li>Начинайте тренировку с разминки в зоне восстановления (50-60%)</li>
              <li>Постепенно повышайте интенсивность до целевой зоны</li>
              <li>Для достижения наилучших результатов проводите основную часть тренировки в целевой зоне</li>
              <li>Завершайте тренировку в зоне восстановления</li>
            </ul>
            <p className="mt-3 text-xs text-blue-600 dark:text-blue-400">
              Помните, что расчет является приблизительным. При возникновении неприятных ощущений или сильной одышки немедленно снизьте интенсивность тренировки.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TargetZonesVisualization;